// Custom hook for automatic assessment progress saving

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  AssessmentProgress,
  SaveStatus,
} from '../types/assessment-progress';
import {
  createEmptyProgress,
  saveProgress as saveToStorage,
  loadProgress as loadFromStorage,
  clearProgress,
  hasMeaningfulData,
  onStorageChange,
} from '../utils/assessmentStorage';
import { useBackendSync } from './useBackendSync';

interface UseAutoSaveOptions {
  userId?: string | null;
  debounceMs?: number;
  onSaveSuccess?: () => void;
  onSaveError?: (error: Error) => void;
  onProgressLoaded?: (progress: AssessmentProgress) => void;
}

interface UpdateOptions {
  immediate?: boolean;
}

interface ExtendedSaveStatus extends SaveStatus {
  isBackendSyncing?: boolean;
  lastBackendSync?: Date | null;
  hasPendingChanges?: boolean;
  backendSyncError?: Error | null;
}

interface UseAutoSaveReturn {
  progress: AssessmentProgress;
  saveStatus: ExtendedSaveStatus;
  updateProgress: (
    updater: (prev: AssessmentProgress) => AssessmentProgress,
    options?: UpdateOptions
  ) => void;
  saveNow: () => Promise<boolean>;
  clearAllProgress: () => Promise<void>;
  loadSavedProgress: () => Promise<AssessmentProgress | null>;
  hasExistingProgress: boolean;
  isUserAuthenticated: boolean;
}

/**
 * Custom hook for managing assessment progress with auto-save functionality
 * 
 * Features:
 * - Immediate saving for user actions (clicks, selections) - 0ms delay
 * - Debounced saving for text inputs - 500ms delay (configurable)
 * - Dual persistence: localStorage (fast) + Backend (cross-device)
 * - Manual save trigger
 * - Progress loading and validation
 * - Multi-tab synchronization
 * - Error handling
 * 
 * Usage:
 * - For immediate save (selections, clicks): updateProgress(..., { immediate: true })
 * - For debounced save (text input): updateProgress(...) // No options
 */
export const useAutoSave = (options: UseAutoSaveOptions = {}): UseAutoSaveReturn => {
  const {
    userId = null,
    debounceMs = 500, // Reduced from 2000 to 500ms for faster auto-save
    onSaveSuccess,
    onSaveError,
    onProgressLoaded,
  } = options;

  // Initialize backend sync hook
  const {
    syncStatus: backendSyncStatus,
    syncToBackend,
    syncImmediate: syncBackendImmediate,
    loadFromBackend,
    clearBackend,
    isUserAuthenticated
  } = useBackendSync({
    debounceMs: 1000, // Backend sync with 1s debounce (slower than localStorage)
    onSyncSuccess: () => console.log('✅ Backend sync successful'),
    onSyncError: (error) => console.error('❌ Backend sync error:', error)
  });

  const [progress, setProgress] = useState<AssessmentProgress>(() => {
    const loaded = loadFromStorage();
    if (loaded && onProgressLoaded) {
      onProgressLoaded(loaded);
    }
    return loaded || createEmptyProgress(userId);
  });

  const [saveStatus, setSaveStatus] = useState<ExtendedSaveStatus>({
    isSaving: false,
    lastSaved: null,
    error: null,
    isBackendSyncing: false,
    lastBackendSync: null,
    hasPendingChanges: false,
    backendSyncError: null
  });

  const [hasExistingProgress, setHasExistingProgress] = useState(() => {
    const loaded = loadFromStorage();
    return loaded !== null && hasMeaningfulData(loaded);
  });

  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSaveRef = useRef<string>('');

  /**
   * Save progress to localStorage immediately
   */
  const saveNow = useCallback(async (): Promise<boolean> => {
    try {
      setSaveStatus((prev) => ({ ...prev, isSaving: true, error: null }));

      // Serialize current progress for comparison
      const currentSerialized = JSON.stringify(progress);

      // Skip if nothing changed
      if (currentSerialized === lastSaveRef.current) {
        setSaveStatus((prev) => ({ ...prev, isSaving: false }));
        return true;
      }

      const success = saveToStorage(progress);

      if (success) {
        lastSaveRef.current = currentSerialized;
        setSaveStatus({
          isSaving: false,
          lastSaved: new Date(),
          error: null,
        });
        
        if (onSaveSuccess) {
          onSaveSuccess();
        }

        return true;
      } else {
        throw new Error('Failed to save progress to localStorage');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error in saveNow:', error);
      
      setSaveStatus({
        isSaving: false,
        lastSaved: null,
        error: errorMessage,
      });

      if (onSaveError && error instanceof Error) {
        onSaveError(error);
      }

      return false;
    }
  }, [progress, onSaveSuccess, onSaveError]);

  /**
   * Update progress with optional immediate save or debounced auto-save
   * Syncs to BOTH localStorage (fast) and backend (persistent)
   */
  const updateProgress = useCallback(
    (
      updater: (prev: AssessmentProgress) => AssessmentProgress,
      options?: UpdateOptions
    ) => {
      setProgress((prev) => {
        const updated = updater(prev);
        
        // Clear existing timeout
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }

        // Check if immediate save is requested
        if (options?.immediate) {
          // ========================================
          // IMMEDIATE SAVE - No debounce
          // ========================================
          setSaveStatus((prevStatus) => ({ ...prevStatus, isSaving: true }));
          
          // 1. Save to localStorage IMMEDIATELY
          const success = saveToStorage(updated);
          
          if (success) {
            setSaveStatus((prevStatus) => ({
              ...prevStatus,
              isSaving: false,
              lastSaved: new Date(),
              error: null,
            }));
            lastSaveRef.current = JSON.stringify(updated);
            
            if (onSaveSuccess) {
              onSaveSuccess();
            }
          } else {
            setSaveStatus((prevStatus) => ({
              ...prevStatus,
              isSaving: false,
              lastSaved: null,
              error: 'Failed to save',
            }));
          }
          
          // 2. Sync to backend IMMEDIATELY (if authenticated)
          syncBackendImmediate(updated);
          
        } else {
          // ========================================
          // DEBOUNCED SAVE - Wait before saving
          // ========================================
          setSaveStatus((prevStatus) => ({ ...prevStatus, isSaving: true }));
          
          saveTimeoutRef.current = setTimeout(() => {
            // 1. Save to localStorage (500ms debounce)
            const success = saveToStorage(updated);
            
            if (success) {
              setSaveStatus((prevStatus) => ({
                ...prevStatus,
                isSaving: false,
                lastSaved: new Date(),
                error: null,
              }));
              lastSaveRef.current = JSON.stringify(updated);
              
              if (onSaveSuccess) {
                onSaveSuccess();
              }
            } else {
              setSaveStatus((prevStatus) => ({
                ...prevStatus,
                isSaving: false,
                lastSaved: null,
                error: 'Failed to save',
              }));
            }
          }, debounceMs);
          
          // 2. Sync to backend (1000ms debounce)
          syncToBackend(updated);
        }

        return updated;
      });
    },
    [debounceMs, onSaveSuccess, syncToBackend, syncBackendImmediate]
  );

  /**
   * Load saved progress with backend priority
   * Strategy: Backend (if authenticated) > localStorage
   */
  const loadSavedProgress = useCallback(async (): Promise<AssessmentProgress | null> => {
    try {
      console.log('📥 Loading progress...');
      
      // 1. Try loading from backend first (if authenticated)
      if (isUserAuthenticated) {
        const backendProgress = await loadFromBackend();
        
        if (backendProgress) {
          console.log('✅ Progress loaded from backend');
          setProgress(backendProgress);
          setHasExistingProgress(hasMeaningfulData(backendProgress));
          
          // Sync to localStorage for offline access
          saveToStorage(backendProgress);
          
          if (onProgressLoaded) {
            onProgressLoaded(backendProgress);
          }
          
          return backendProgress;
        }
      }
      
      // 2. Fallback to localStorage
      const localProgress = loadFromStorage();
      
      if (localProgress) {
        console.log('✅ Progress loaded from localStorage');
        setProgress(localProgress);
        setHasExistingProgress(hasMeaningfulData(localProgress));
        
        // If authenticated, sync local → backend
        if (isUserAuthenticated) {
          console.log('🔄 Syncing local progress to backend...');
          syncBackendImmediate(localProgress);
        }
        
        if (onProgressLoaded) {
          onProgressLoaded(localProgress);
        }
        
        return localProgress;
      }
      
      console.log('ℹ️ No progress found');
      return null;
      
    } catch (error) {
      console.error('Error loading saved progress:', error);
      return null;
    }
  }, [onProgressLoaded, isUserAuthenticated, loadFromBackend, syncBackendImmediate]);

  /**
   * Clear all progress data from BOTH localStorage and backend
   */
  const clearAllProgress = useCallback(async () => {
    try {
      console.log('🗑️ Clearing all progress...');
      
      // 1. Clear localStorage
      clearProgress();
      
      // 2. Clear backend (if authenticated)
      if (isUserAuthenticated) {
        await clearBackend();
      }
      
      // 3. Reset state
      setProgress(createEmptyProgress(userId));
      setHasExistingProgress(false);
      setSaveStatus({
        isSaving: false,
        lastSaved: null,
        error: null,
        isBackendSyncing: false,
        lastBackendSync: null,
        hasPendingChanges: false,
        backendSyncError: null
      });
      lastSaveRef.current = '';
      
      console.log('✅ All progress cleared');
    } catch (error) {
      console.error('Error clearing progress:', error);
    }
  }, [userId, isUserAuthenticated, clearBackend]);

  /**
   * Cleanup on unmount - cancel any pending saves
   */
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Listen to storage changes for multi-tab sync
   */
  useEffect(() => {
    const unsubscribe = onStorageChange((updatedProgress) => {
      if (updatedProgress) {
        setProgress(updatedProgress);
      } else {
        setProgress(createEmptyProgress(userId));
        setHasExistingProgress(false);
      }
    });

    return unsubscribe;
  }, [userId]);

  /**
   * Save on unmount
   */
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      // Final save on unmount
      if (hasMeaningfulData(progress)) {
        saveToStorage(progress);
      }
    };
  }, [progress]);

  /**
   * Sync backend sync status with local save status
   */
  useEffect(() => {
    setSaveStatus((prevStatus) => ({
      ...prevStatus,
      isBackendSyncing: backendSyncStatus.isSyncing,
      lastBackendSync: backendSyncStatus.lastSyncAt,
      hasPendingChanges: backendSyncStatus.hasPendingChanges,
      backendSyncError: backendSyncStatus.syncError
    }));
  }, [backendSyncStatus]);

  return {
    progress,
    saveStatus,
    updateProgress,
    saveNow,
    clearAllProgress,
    loadSavedProgress,
    hasExistingProgress,
    isUserAuthenticated
  };
};

export default useAutoSave;

